import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { AppStartupService, BaseComponent } from '@ridango/core';
import Env from '@ridango/core/env';
import { ApplicationFacade, DeviceNotificationService, NotificationFacade } from '@ridango/components';
import { ActivatedRoute, Params } from '@angular/router';
import { MessagesService, MessageStatus } from '../../../services/messages.service';
import { take, takeUntil } from 'rxjs/operators';

@Component({
    templateUrl: './messages-detail-page.component.html',
    styleUrls: ['./messages-detail-page.component.scss']
})
export class MessagesDetailPageComponent extends BaseComponent implements OnInit, OnDestroy {
    readonly assetsPath$ = this.applicationFacade.assetsPath$;
    public currentMessage: any = [];
    protected messageRead = true;
    readonly DATE_TIME_FORMAT = this.env.PRIMARY_DATE_TIME_FORMAT;
    readonly TIMEZONE = this.appStartupService.getTimezone();

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(LOCALE_ID) public locale: string,
        private applicationFacade: ApplicationFacade,
        private translate: TranslateService,
        public appStartupService: AppStartupService,
        private notification: NotificationFacade,
        private messagesService: MessagesService,
        private route: ActivatedRoute,
        private deviceNotificationService: DeviceNotificationService
    ) {
        super();
    }

    ngOnInit() {
        this.locale = this.translate.currentLang;
        this.route.queryParams.subscribe((params: Params) => {
            if (params?.push && params?.messageId) {
                this.openMessageFromPush(params);
            } else {
                this.currentMessage = params;
                if (JSON.parse(this.currentMessage.isRead)) {
                    return;
                }
                this.messagesService
                    .markMessageAsReadUnread(this.currentMessage.id, MessageStatus.READ)
                    .pipe(take(1), takeUntil(this.componentDestroyed$))
                    .subscribe(() => {
                        this.deviceNotificationService.decreaseBadgeCount();
                    });
            }
        });
    }

    openMessageFromPush(params: Params) {
        const messageId = params.messageId;
        this.messagesService
            .fetchMessagesById(messageId)
            .pipe(take(1))
            .subscribe({
                next: (response: { data: any[]; meta: any }) => {
                    const messages = response?.data ?? [];

                    // because customer may have several FCM tokens, by customerId we may receive one to many messages
                    // mark all messages as read, but open only one of them (their duplicates by content)
                    messages.forEach((message) => {
                        if (message.isRead) {
                            return;
                        }
                        this.messagesService
                            .markMessageAsReadUnread(message.id, MessageStatus.READ)
                            .pipe(take(1))
                            .subscribe(() => {
                                this.deviceNotificationService.decreaseBadgeCount();
                            });
                    });

                    this.currentMessage = messages.find((message) => message.id === messageId) || messages[0];

                    if (this.currentMessage) {
                        this.messageRead = true;
                    } else {
                        console.warn('Message not found');
                        this.notification.error(this.translate.instant('MESSAGE_NOT_FOUND'));
                    }
                },
                error: (err) => {
                    console.error('Failed to fetch message', err);
                    this.notification.error(this.translate.instant('MESSAGE_NOT_FOUND'));
                }
            });
    }

    markAsReadUnread() {
        const status = this.messageRead ? MessageStatus.UNREAD : MessageStatus.READ;
        this.notification.removeAll();
        this.messagesService.markMessageAsReadUnread(this.currentMessage.id, status).subscribe(() => {
            if (status === MessageStatus.READ) {
                this.deviceNotificationService.decreaseBadgeCount();
            } else {
                this.deviceNotificationService.increaseBadgeCount();
            }
            const message = this.messageRead ? 'MESSAGE_MARKED_AS_UNREAD' : 'MESSAGE_MARKED_AS_READ';
            this.notification.success(this.translate.instant(message));
            this.messageRead = !this.messageRead;
        });
    }
}
